import React, { useState, useEffect } from 'react';

interface HeartbeatsResponse {
  'mercury-backend': {
    status: string;
    pid?: number;
    memory_usage_mb?: number;
    cpu_percent?: number;
    uptime_seconds?: number;
    port?: number;
    timestamp: string;
    database?: string;
    connected?: boolean;
    realtime_connected?: boolean;
  };
  'mercury-stock': {
    status: string;
    pid?: number;
    memory_usage_mb?: number;
    cpu_percent?: number;
    uptime_seconds?: number;
    port?: number;
    timestamp: string;
  };
  'mercury-crypto': {
    status: string;
    pid?: number;
    memory_usage_mb?: number;
    cpu_percent?: number;
    uptime_seconds?: number;
    port?: number;
    timestamp: string;
  };
  'mercury-kis-open-trading-api': {
    status: string;
    pid?: number;
    memory_usage_mb?: number;
    cpu_percent?: number;
    uptime_seconds?: number;
    port?: number;
    timestamp: string;
  };
  'mercury-mysql': {
    status: string;
    database: string;
    connected: boolean;
    realtime_connected: boolean;
    ping_time: string;
    error?: string;
    timestamp: string;
  };
  'mercury-redis': {
    status: string;
    service: string;
    ping_time: string;
    error?: string;
    timestamp: string;
  };
  'mercury-mongodb': {
    status: string;
    database: string;
    connected: boolean;
    realtime_connected: boolean;
    ping_time: string;
    error?: string;
    timestamp: string;
  };
  'mercury-rabbitmq': {
    status: string;
    service: string;
    ping_time: string;
    error?: string;
    timestamp: string;
  };
}

const Heartbeats: React.FC = () => {
    const [connectionInfo, setConnectionInfo] = useState<HeartbeatsResponse>({
        'mercury-backend': { status: 'loading', timestamp: '' },
        'mercury-stock': { status: 'loading', timestamp: '' },
        'mercury-crypto': { status: 'loading', timestamp: '' },
        'mercury-kis-open-trading-api': { status: 'loading', timestamp: '' },
        'mercury-mysql': { status: 'loading', database: '', connected: false, realtime_connected: false, ping_time: '', error: '', timestamp: '' },
        'mercury-redis': { status: 'loading', service: 'redis', ping_time: '', error: '', timestamp: '' },
        'mercury-mongodb': { status: 'loading', database: '', connected: false, realtime_connected: false, ping_time: '', error: '', timestamp: '' },
        'mercury-rabbitmq': { status: 'loading', service: 'rabbitmq', ping_time: '', error: '', timestamp: '' },
    });
    const [loading, setLoading] = useState<boolean>(true);

    const checkHeartbeats = async () => {
        try {
            setLoading(true);
            
            // 각 서버별로 개별 heartbeat 요청
            const [analyserRes, stockRes, cryptoRes, kisApiRes, mysqlRes, redisRes, mongodbRes, rabbitmqRes] = await Promise.allSettled([
                fetch('/mercury-backend/heartbeat', { 
                    method: 'GET',
                    signal: AbortSignal.timeout(5000) // 5초 타임아웃
                }).catch(err => {
                    console.error('mercury-backend heartbeat error:', err);
                    throw err;
                }),
                // 프로덕션에서는 백엔드를 통해 Lambda 함수 존재 여부 확인
                fetch(process.env.REACT_APP_ENV === 'mercury-frontend-prod' 
                    ? '/mercury-backend/heartbeat-stock'
                    : '/mercury-stock/heartbeat', { 
                    method: 'GET',
                    signal: AbortSignal.timeout(5000)
                }).catch(err => {
                    console.error('mercury-stock heartbeat error:', err);
                    throw err;
                }),
                fetch('/mercury-crypto/heartbeat', { 
                    method: 'GET',
                    signal: AbortSignal.timeout(5000)
                }).catch(err => {
                    console.error('mercury-crypto heartbeat error:', err);
                    throw err;
                }),
                fetch('/mercury-kis-open-trading-api/heartbeat', { 
                    method: 'GET',
                    signal: AbortSignal.timeout(5000)
                }).catch(err => {
                    console.error('mercury-kis-open-trading-api heartbeat error:', err);
                    throw err;
                }),
                fetch('/mercury-backend/heartbeat-mysql', { 
                    method: 'GET',
                    signal: AbortSignal.timeout(5000)
                }).catch(err => {
                    console.error('MySQL heartbeat error:', err);
                    throw err;
                }),
                fetch('/mercury-backend/heartbeat-redis', { 
                    method: 'GET',
                    signal: AbortSignal.timeout(5000)
                }).catch(err => {
                    console.error('Redis heartbeat error:', err);
                    throw err;
                }),
                fetch('/mercury-backend/heartbeat-mongodb', { 
                    method: 'GET',
                    signal: AbortSignal.timeout(5000)
                }).catch(err => {
                    console.error('MongoDB heartbeat error:', err);
                    throw err;
                }),
                fetch('/mercury-backend/heartbeat-rabbitmq', { 
                    method: 'GET',
                    signal: AbortSignal.timeout(5000)
                }).catch(err => {
                    console.error('RabbitMQ heartbeat error:', err);
                    throw err;
                })
            ]);
            
            // mercury-backend 상태 확인
            const backendStatus = analyserRes.status === 'fulfilled' && 
                                  analyserRes.value && 
                                  analyserRes.value.ok && 
                                  analyserRes.value.status === 200 ? 'healthy' : 'error';
            const backendPort = backendStatus === 'healthy' ? 8080 : 0;
            
            // mercury-stock 상태 확인
            let stockStatus = 'error';
            if (stockRes.status === 'fulfilled' && stockRes.value && stockRes.value.ok) {
                try {
                    const stockData = await stockRes.value.json();
                    stockStatus = stockData.status === 'healthy' ? 'healthy' : 'error';
                } catch (err) {
                    console.error('Error parsing stock status response:', err);
                    stockStatus = stockRes.value.status === 200 ? 'healthy' : 'error';
                }
            }
            const stockPort = stockStatus === 'healthy' ? (process.env.REACT_APP_ENV === 'mercury-frontend-prod' ? 0 : 8081) : 0;
            
            // mercury-crypto 상태 확인 (더 엄격한 검증)
            const cryptoStatus = cryptoRes.status === 'fulfilled' && 
                                cryptoRes.value && 
                                cryptoRes.value.ok && 
                                cryptoRes.value.status === 200 ? 'healthy' : 'error';
            const cryptoPort = cryptoStatus === 'healthy' ? 8082 : 0;
            
            // 디버깅: mercury-crypto 상태 로깅 (개발 환경에서만)
            if (process.env.NODE_ENV === 'development') {
                if (cryptoRes.status === 'rejected') {
                    console.warn('mercury-crypto heartbeat rejected:', cryptoRes.reason);
                } else if (cryptoRes.status === 'fulfilled') {
                    console.log('mercury-crypto heartbeat response:', {
                        ok: cryptoRes.value?.ok,
                        status: cryptoRes.value?.status,
                        statusText: cryptoRes.value?.statusText
                    });
                }
            }
            
            // mercury-kis-open-trading-api 상태 확인
            const kisApiStatus = kisApiRes.status === 'fulfilled' && 
                                kisApiRes.value && 
                                kisApiRes.value.ok && 
                                kisApiRes.value.status === 200 ? 'healthy' : 'error';
            const kisApiPort = kisApiStatus === 'healthy' ? 8000 : 0;

            // MySQL 상태 확인
            let mysqlStatus: HeartbeatsResponse['mercury-mysql'] = { status: 'error', database: '', connected: false, realtime_connected: false, ping_time: '', error: '', timestamp: '' };
            if (mysqlRes.status === 'fulfilled' && mysqlRes.value.ok) {
                try {
                    const mysqlData = await mysqlRes.value.json();
                    mysqlStatus = {
                        status: mysqlData.status || 'error',
                        database: mysqlData.database || '',
                        connected: mysqlData.connected || false,
                        realtime_connected: mysqlData.realtime_connected || false,
                        ping_time: mysqlData.ping_time || '',
                        error: mysqlData.error,
                        timestamp: mysqlData.timestamp || new Date().toISOString()
                    };
                } catch (err) {
                    console.error('Error parsing MySQL status response:', err);
                }
            }

            // Redis 상태 확인
            let redisStatus: HeartbeatsResponse['mercury-redis'] = { status: 'error', service: 'redis', ping_time: '', error: '', timestamp: '' };
            if (redisRes.status === 'fulfilled' && redisRes.value.ok) {
                try {
                    const redisData = await redisRes.value.json();
                    redisStatus = {
                        status: redisData.status || 'error',
                        service: redisData.service || 'redis',
                        ping_time: redisData.ping_time || '',
                        error: redisData.error,
                        timestamp: redisData.timestamp || new Date().toISOString()
                    };
                } catch (err) {
                    console.error('Error parsing Redis status response:', err);
                }
            }

            // MongoDB 상태 확인
            let mongodbStatus: HeartbeatsResponse['mercury-mongodb'] = { status: 'error', database: '', connected: false, realtime_connected: false, ping_time: '', error: '', timestamp: '' };
            if (mongodbRes.status === 'fulfilled' && mongodbRes.value.ok) {
                try {
                    const mongodbData = await mongodbRes.value.json();
                    mongodbStatus = {
                        status: mongodbData.status || 'error',
                        database: mongodbData.database || '',
                        connected: mongodbData.connected || false,
                        realtime_connected: mongodbData.realtime_connected || false,
                        ping_time: mongodbData.ping_time || '',
                        error: mongodbData.error,
                        timestamp: mongodbData.timestamp || new Date().toISOString()
                    };
                } catch (err) {
                    console.error('Error parsing MongoDB status response:', err);
                }
            }

            // RabbitMQ 상태 확인
            let rabbitmqStatus: HeartbeatsResponse['mercury-rabbitmq'] = { status: 'error', service: 'rabbitmq', ping_time: '', error: '', timestamp: '' };
            if (rabbitmqRes.status === 'fulfilled' && rabbitmqRes.value.ok) {
                try {
                    const rabbitmqData = await rabbitmqRes.value.json();
                    rabbitmqStatus = {
                        status: rabbitmqData.status || 'error',
                        service: rabbitmqData.service || 'rabbitmq',
                        ping_time: rabbitmqData.ping_time || '',
                        error: rabbitmqData.error,
                        timestamp: rabbitmqData.timestamp || new Date().toISOString()
                    };
                } catch (err) {
                    console.error('Error parsing RabbitMQ status response:', err);
                }
            }

            
            const result: HeartbeatsResponse = {
                'mercury-backend': {
                    status: backendStatus,
                    pid: 0,
                    memory_usage_mb: 0,
                    cpu_percent: 0,
                    uptime_seconds: 0,
                    port: backendPort,
                    timestamp: new Date().toISOString()
                },
                'mercury-stock': {
                    status: stockStatus,
                    pid: 0,
                    memory_usage_mb: 0,
                    cpu_percent: 0,
                    uptime_seconds: 0,
                    port: stockPort,
                    timestamp: new Date().toISOString()
                },
                'mercury-crypto': {
                    status: cryptoStatus,
                    pid: 0,
                    memory_usage_mb: 0,
                    cpu_percent: 0,
                    uptime_seconds: 0,
                    port: cryptoPort,
                    timestamp: new Date().toISOString()
                },
                'mercury-kis-open-trading-api': {
                    status: kisApiStatus,
                    pid: 0,
                    memory_usage_mb: 0,
                    cpu_percent: 0,
                    uptime_seconds: 0,
                    port: kisApiPort,
                    timestamp: new Date().toISOString()
                },
                'mercury-mysql': mysqlStatus,
                'mercury-redis': redisStatus,
                'mercury-mongodb': mongodbStatus,
                'mercury-rabbitmq': rabbitmqStatus,
            };
            
            setConnectionInfo(result);
        } catch (err) {
            console.error('Error checking connections:', err);
            // 개별 서비스별로 에러 상태 설정
            setConnectionInfo({
                'mercury-backend': { status: 'error', timestamp: '' },
                'mercury-stock': { status: 'error', timestamp: '' },
                'mercury-crypto': { status: 'error', timestamp: '' },
                'mercury-kis-open-trading-api': { status: 'error', timestamp: '' },
                'mercury-mysql': { status: 'error', database: '', connected: false, realtime_connected: false, ping_time: '', error: '', timestamp: '' },
                'mercury-redis': { status: 'error', service: 'redis', ping_time: '', error: '', timestamp: '' },
                'mercury-mongodb': { status: 'error', database: '', connected: false, realtime_connected: false, ping_time: '', error: '', timestamp: '' },
                'mercury-rabbitmq': { status: 'error', service: 'rabbitmq', ping_time: '', error: '', timestamp: '' },
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        checkHeartbeats();
        
        // 30초마다 자동 체크
        const interval = setInterval(checkHeartbeats, 30000);
        
        return () => clearInterval(interval);
    }, []);

    // 로딩 중이거나 데이터가 없으면 아무것도 렌더링하지 않음
    if (loading || !connectionInfo) {
        return null;
    }

    return (
        <div style={{ 
            width: '100%',
            background: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
            padding: '16px'
        }}>
            <div style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '12px',
                marginBottom: '16px'
            }}>
                    <div style={{
                        backgroundColor: '#2d3748',
                        border: '1px solid #4a5568',
                        borderRadius: '6px',
                        padding: '10px',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                    }}>
                        <div style={{ 
                            fontSize: '9px', 
                            fontWeight: '600', 
                            color: '#e6edf3',
                            marginBottom: '4px'
                        }}>
                            mercury-backend
                        </div>
                        <div style={{ 
                            color: connectionInfo['mercury-backend']?.status === 'healthy' ? '#4ade80' : 
                                   connectionInfo['mercury-backend']?.status === 'loading' ? '#fbbf24' : '#f87171',
                            fontSize: '8px',
                            fontWeight: '500',
                            marginBottom: '3px'
                        }}>
                            {connectionInfo['mercury-backend']?.status === 'loading' ? '⏳ Checking' : 
                             connectionInfo['mercury-backend']?.status === 'healthy' ? 'Connected' : 'Disconnected'}
                        </div>
                    </div>

                    {/* Mercury Stock */}
                    <div style={{
                        backgroundColor: '#2d3748',
                        border: '1px solid #4a5568',
                        borderRadius: '6px',
                        padding: '10px',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                    }}>
                        <div style={{ 
                            fontSize: '9px', 
                            fontWeight: '600', 
                            color: '#e6edf3',
                            marginBottom: '4px'
                        }}>
                            mercury-stock
                        </div>
                        <div style={{ 
                            color: connectionInfo['mercury-stock']?.status === 'healthy' ? '#4ade80' : 
                                   connectionInfo['mercury-stock']?.status === 'loading' ? '#fbbf24' : '#f87171',
                            fontSize: '8px',
                            fontWeight: '500',
                            marginBottom: '3px'
                        }}>
                            {connectionInfo['mercury-stock']?.status === 'loading' ? '⏳ Checking' : 
                             connectionInfo['mercury-stock']?.status === 'healthy' ? 'Connected' : 'Disconnected'}
                        </div>
                    </div>

                    {/* Mercury Crypto - Temporarily hidden */}
                    {/* <div style={{
                        backgroundColor: '#2d3748',
                        border: '1px solid #4a5568',
                        borderRadius: '6px',
                        padding: '10px',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                    }}>
                        <div style={{ 
                            fontSize: '9px', 
                            fontWeight: '600', 
                            color: '#e6edf3',
                            marginBottom: '4px'
                        }}>
                            mercury-crypto
                        </div>
                        <div style={{ 
                            color: connectionInfo['mercury-crypto']?.status === 'healthy' ? '#4ade80' : 
                                   connectionInfo['mercury-crypto']?.status === 'loading' ? '#fbbf24' : '#f87171',
                            fontSize: '8px',
                            fontWeight: '500',
                            marginBottom: '3px'
                        }}>
                            {connectionInfo['mercury-crypto']?.status === 'loading' ? '⏳ Checking' : 
                             connectionInfo['mercury-crypto']?.status === 'healthy' ? 'Connected' : 'Disconnected'}
                        </div>
                    </div> */}

                    {/* Mercury KIS Open Trading API - Temporarily hidden */}
                    {/* <div style={{
                        backgroundColor: '#2d3748',
                        border: '1px solid #4a5568',
                        borderRadius: '6px',
                        padding: '10px',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                    }}>
                        <div style={{ 
                            fontSize: '9px', 
                            fontWeight: '600', 
                            color: '#e6edf3',
                            marginBottom: '4px'
                        }}>
                            mercury-kis-open-trading-api
                        </div>
                        <div style={{ 
                            color: connectionInfo['mercury-kis-open-trading-api']?.status === 'healthy' ? '#4ade80' : 
                                   connectionInfo['mercury-kis-open-trading-api']?.status === 'loading' ? '#fbbf24' : '#f87171',
                            fontSize: '8px',
                            fontWeight: '500',
                            marginBottom: '3px'
                        }}>
                            {connectionInfo['mercury-kis-open-trading-api']?.status === 'loading' ? '⏳ Checking' : 
                             connectionInfo['mercury-kis-open-trading-api']?.status === 'healthy' ? 'Connected' : 'Disconnected'}
                        </div>
                    </div> */}

                    {/* MySQL */}
                    <div style={{
                        backgroundColor: '#2d3748',
                        border: '1px solid #4a5568',
                        borderRadius: '6px',
                        padding: '10px',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                    }}>
                        <div style={{ 
                            fontSize: '9px', 
                            fontWeight: '600', 
                            color: '#e6edf3',
                            marginBottom: '4px'
                        }}>
                            mercury-mysql
                        </div>
                        <div style={{ 
                            color: connectionInfo['mercury-mysql']?.status === 'healthy' ? '#4ade80' : 
                                   connectionInfo['mercury-mysql']?.status === 'loading' ? '#fbbf24' : '#f87171',
                            fontSize: '8px',
                            fontWeight: '500',
                            marginBottom: '3px'
                        }}>
                            {connectionInfo['mercury-mysql']?.status === 'loading' ? '⏳ Checking' : 
                             connectionInfo['mercury-mysql']?.status === 'healthy' ? 'Connected' : 'Disconnected'}
                        </div>
                    </div>

                    {/* Redis */}
                    <div style={{
                        backgroundColor: '#2d3748',
                        border: '1px solid #4a5568',
                        borderRadius: '6px',
                        padding: '10px',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                    }}>
                        <div style={{ 
                            fontSize: '9px', 
                            fontWeight: '600', 
                            color: '#e6edf3',
                            marginBottom: '4px'
                        }}>
                            mercury-redis
                        </div>
                        <div style={{ 
                            color: connectionInfo['mercury-redis']?.status === 'healthy' ? '#4ade80' : 
                                   connectionInfo['mercury-redis']?.status === 'loading' ? '#fbbf24' : '#f87171',
                            fontSize: '8px',
                            fontWeight: '500',
                            marginBottom: '3px'
                        }}>
                            {connectionInfo['mercury-redis']?.status === 'loading' ? '⏳ Checking' : 
                             connectionInfo['mercury-redis']?.status === 'healthy' ? 'Connected' : 'Disconnected'}
                        </div>
                    </div>

                    {/* MongoDB */}
                    <div style={{
                        backgroundColor: '#2d3748',
                        border: '1px solid #4a5568',
                        borderRadius: '6px',
                        padding: '10px',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                    }}>
                        <div style={{ 
                            fontSize: '9px', 
                            fontWeight: '600', 
                            color: '#e6edf3',
                            marginBottom: '4px'
                        }}>
                            mercury-mongodb
                        </div>
                        <div style={{ 
                            color: connectionInfo['mercury-mongodb']?.status === 'healthy' ? '#4ade80' : 
                                   connectionInfo['mercury-mongodb']?.status === 'loading' ? '#fbbf24' : '#f87171',
                            fontSize: '8px',
                            fontWeight: '500',
                            marginBottom: '3px'
                        }}>
                            {connectionInfo['mercury-mongodb']?.status === 'loading' ? '⏳ Checking' : 
                             connectionInfo['mercury-mongodb']?.status === 'healthy' ? 'Connected' : 'Disconnected'}
                        </div>
                    </div>

                    {/* RabbitMQ */}
                    <div style={{
                        backgroundColor: '#2d3748',
                        border: '1px solid #4a5568',
                        borderRadius: '6px',
                        padding: '10px',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                    }}>
                        <div style={{ 
                            fontSize: '9px', 
                            fontWeight: '600', 
                            color: '#e6edf3',
                            marginBottom: '4px'
                        }}>
                            mercury-rabbitmq
                        </div>
                        <div style={{ 
                            color: connectionInfo['mercury-rabbitmq']?.status === 'healthy' ? '#4ade80' : 
                                   connectionInfo['mercury-rabbitmq']?.status === 'loading' ? '#fbbf24' : '#f87171',
                            fontSize: '8px',
                            fontWeight: '500',
                            marginBottom: '3px'
                        }}>
                            {connectionInfo['mercury-rabbitmq']?.status === 'loading' ? '⏳ Checking' : 
                             connectionInfo['mercury-rabbitmq']?.status === 'healthy' ? 'Connected' : 'Disconnected'}
                        </div>
                    </div>


                </div>
            
            {/* 새로고침 버튼과 자동 갱신 정보 */}
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginTop: '16px',
                paddingTop: '12px',
                borderTop: '1px solid #4a5568'
            }}>
                <div style={{ 
                    fontSize: '10px', 
                    color: '#9ca3af' 
                }}>
                    Auto refresh every 30s
                </div>
                <button
                    onClick={checkHeartbeats}
                    style={{
                        padding: '6px 12px',
                        backgroundColor: '#4ade80',
                        color: '#1a1a1a',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                        transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = '#22c55e';
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = '#4ade80';
                    }}
                >
                    Refresh
                </button>
            </div>
        </div>
    );
};

export default Heartbeats;